<template>
  <div :class="[$b(), 'ignore-theme']">
    <div class="global-wrapper">
      <div :class="$b('grid')">
        <div :class="$b('heading-wrapper')">
          <BaseHeading
            tag="h4"
            size="h4"
            :class="$b('heading')"
          >
            {{ title }}
          </BaseHeading>

          <ul :class="$b('pagination', { mobileOnly: true })">
            <li
              v-for="(quote, index) in visibleQuotes"
              :key="quote.id"
            >
              <TimerButton
                :is-active="activeIndex === index"
                :gradient-id="`mobile-${index}`"
                @click="transitionToSlide(index)"
              />
            </li>
          </ul>
        </div>

        <div :class="$b('swiper-container')">
          <div id="testimonial-quotes">
            <Swiper
              :class="$b('wrapper')"
              :a11y="true"
              :autoplay="{
                delay: slideDuration,
                disableOnInteraction: false,
                waitForTransition: false
              }"
              :init="false"
              effect="fade"
              :fade-effect="{
                crossFade: true
              }"
              :modules="[Autoplay, EffectFade]"
              :keyboard="{
                enabled: true
              }"
              :loop="true"
              :loop-prevents-slide="false"
              :speed="slideTransitionDuration"
              @swiper="handleQuoteSwiperReady"
              @slide-change="event => handleSlideChange(event, 'quote')"
            >
              <SwiperSlide
                v-for="quote in visibleQuotes"
                :key="quote.id"
                :class="$b('slide')"
              >
                <BaseHeading
                  tag="h3"
                  size="h3"
                  :class="$b('quote')"
                >
                  {{ quote.quote }}
                </BaseHeading>
              </SwiperSlide>
            </Swiper>
          </div>
          <div
            id="testimonial-images"
            :class="$b('image-container')"
          >
            <Swiper
              :class="$b('wrapper')"
              :a11y="true"
              :autoplay="{
                delay: slideDuration,
                disableOnInteraction: false,
                waitForTransition: false
              }"
              :init="false"
              effect="fade"
              :fade-effect="{
                crossFade: true
              }"
              :modules="[Autoplay, EffectFade]"
              :keyboard="{
                enabled: true
              }"
              :loop="true"
              :loop-prevents-slide="false"
              :speed="slideTransitionDuration"
              @swiper="handleImageSwiperReady"
              @slide-change="event => handleSlideChange(event, 'image')"
            >
              <SwiperSlide
                v-for="quote in visibleQuotes"
                :key="quote.id"
                :class="$b('slide', {
                  landscape: quote.imageAspectRatio > 1,
                  portrait: quote.imageAspectRatio < 1,
                  square: quote.imageAspectRatio == 1,
                })"
              >
                <div :class="$b('image-wrapper')">
                  <img
                    v-if="quote.image"
                    :class="$b('img')"
                    :src="quote.image.src"
                    :alt="quote.image.alt"
                    loading="lazy"
                  />
                </div>
              </SwiperSlide>
            </Swiper>
            <ul :class="$b('pagination', { desktopOnly: true })">
              <li
                v-for="(quote, index) in visibleQuotes"
                :key="quote.id"
              >
                <TimerButton
                  :is-active="activeIndex === index"
                  :gradient-id="`desktop-${index}`"
                  @click="transitionToSlide(index)"
                />
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Autoplay, EffectFade } from 'swiper/modules'

export default {
  name: 'ProductTestimonialQuotes',
  components: {
    Swiper,
    SwiperSlide,
  },
  props: {
    title: { type: String, default: 'In The Press' },
    quotes: { type: Array, required: true },
  },
  setup () {
    return {
      Autoplay,
      EffectFade
    }
  },
  data: () => ({
    quoteSwiper: null,
    imageSwiper: null,
    isReady: false,
    slideDuration: 8000,
    slideTransitionDuration: 300,
    activeIndex: 0
  }),
  computed: {
    visibleQuotes() {
      return this.quotes.map((quote) => ({
        ...quote,
        image: quote.image.responsiveImage,
        imageAspectRatio: quote.image.responsiveImage.aspectRatio,
      }))
    }
  },
  methods: {
    handleQuoteSwiperReady (swiper) {
      this.quoteSwiper = swiper
    },
    handleImageSwiperReady (swiper) {
      this.imageSwiper = swiper
    },
    handleSlideChange (event, source) {
      // Ensure both swipers are in sync
      const duration = this.slideTransitionDuration
      if (source === 'quote') {
        if (this.imageSwiper?.realIndex === event.realIndex) return
        this.imageSwiper?.slideToLoop(event.realIndex, duration)
      } else if (source === 'image') {
        if (this.quoteSwiper?.realIndex === event.realIndex) return
        this.quoteSwiper?.slideToLoop(event.realIndex, duration)
      }

      this.activeIndex = event.realIndex
    },
    transitionToSlide(index) {
      this.quoteSwiper?.slideToLoop(index, this.slideTransitionDuration)
    }
  },
}
</script>

<style lang="postcss">
.ProductTestimonialQuotes {
  @apply bg-white;

  @apply py-[60px]
    min-h-[590px];

  @screen md {
    @apply py-40;
  }

  &__grid {
    @screen md {
      @apply grid grid-cols-3;
    }

    @screen lg {
      @apply grid-cols-2;
    }
  }

  &__heading-wrapper {
    @apply flex items-center justify-between;

    @screen md {
      @apply items-start;
    }
  }

  &__heading {
    @apply t-gradient;
  }

  &__swiper-container {
    @apply overflow-hidden;

    @screen md {
      @apply col-span-2;
    }

    @screen lg {
      @apply col-span-1;
    }
  }

  &__image-container {
    @apply pt-12;

    @screen md {
      @apply flex items-center pt-20;
    }

    .ProductTestimonialQuotes__slide {
      @screen md {
        @apply flex items-center;
      }
    }
  }

  &__img {
    @apply max-h-16;

    @screen md {
      @apply max-h-20 max-w-[290px];
    }
  }

  &__slide {
    @apply bg-white;
  }

  &__wrapper {
    @apply overflow-hidden grow items-center;
  }

  &__image-wrapper {
    @apply md:pl-4;
  }

  &__quote {
    @apply relative
      mt-xl text-off-black;

    &:before {
      content: '\“';
      position: absolute;
      top: -38px;
      font-size: 56px;
      line-height: 1;
    }

    &:after {
      content: '\”';
      margin-left: -4px;
    }

    @screen md {
      @apply mt-0 pl-4
        text-2xl leading-tighter;

      &:before {
        top: 0px;
        left: 0;
        font-size: 32px;
        line-height: 1.1;
      }

      &:after {
        margin-left: -8px;
      }
    }
  }

  &__pagination {
    @apply justify-end;

    &--mobile-only {
      @apply flex;

      @screen md {
        @apply hidden;
      }
    }

    &--desktop-only {
      @apply hidden;

      @screen md {
        @apply flex shrink-0 z-10;
      }
    }
  }
}
</style>
